import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import {
  Button, Form, Input, Modal,
} from 'antd';
import { useUpdateAutomationMutation } from 'automations/services/automationService';
import { useCallback, useEffect } from 'react';

function RenameAutomationContent({
  automationId,
  onClose,
  initialData,
}: {
  automationId: string;
  onClose: () => void;
  initialData?: {
    name: string;
    description: string;
  };
}) {
  const [form] = Form.useForm();

  const [updateAutomation, {
    isLoading: isUpdatingAutomation,
  }] = useUpdateAutomationMutation();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const handleRename = useCallback((values: any) => {
    updateAutomation({
      id: automationId,
      name: values.name,
      description: values.description,
    }).unwrap().then(() => {
      message.success('Action completed successfully');
      onClose();
    }).catch((error: any) => {
      message.error(error?.data?.message ?? 'Failed to update');
    });
  }, [automationId, onClose, updateAutomation]);

  return (
    <Form form={form} onFinish={handleRename}>
      <Form.Item
        name="name"
        rules={[{
          required: true,
        }]}
      >
        <Input placeholder={translate(`Enter #{${TranslationEntity.automation}} Name`, [{ capitalize: true }])} />
      </Form.Item>
      <Form.Item
        name="description"
        rules={[{ required: true }]}
      >
        <Input.TextArea
          rows={2}
          maxLength={500}
          placeholder={translate(`Enter #{${TranslationEntity.automation}} Description`, [{
            capitalize: true,
          }])}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={isUpdatingAutomation}
          disabled={isUpdatingAutomation}
        >
          Rename
        </Button>
      </Form.Item>
    </Form>
  );
}

export default function RenameAutomation({
  isOpen,
  handleClose,
  automationId,
  initialData,
}: {
  isOpen: boolean;
  handleClose: () => void;
  automationId: string;
  initialData?: {
    name: string;
    description: string;
  };
}) {
  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={[]}
      destroyOnClose
      centered
      title={translate(`Rename #{${TranslationEntity.automation}}`, [{ capitalize: true }])}
    >
      <RenameAutomationContent
        automationId={automationId}
        onClose={handleClose}
        initialData={initialData}
      />
    </Modal>
  );
}
